import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CBadge, CButton } from "@coreui/react";
import { ColoredValue } from "@src/components/ColoredValue";
import type * as T from "@src/types";
import { CDataTable } from "@tradingdroid/frontends-components";

interface Props {
  strategies: T.PortfolioTypes.StrategyPerformance[];
}

export const StrategiesTable: React.FC<Props> = ({ strategies }) => {
  const navigate = useNavigate();

  const handleViewExecution = (executionId: number) => {
    navigate(`/execution/${executionId}`);
  };

  const columns = [
    {
      columnKey: "status",
      columnLabel: "Status",
      columnStyle: { width: "80px" },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <CBadge color={strategy.isActive ? "success" : "secondary"} shape="rounded-pill">
          {strategy.isActive ? "Active" : "Closed"}
        </CBadge>
      ),
    },
    {
      columnKey: "name",
      columnLabel: "Execution",
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <Link to={`/execution/${strategy.executionId}`} className="text-decoration-none">
          {strategy.executionName}
        </Link>
      ),
    },
    {
      columnKey: "symbol",
      columnLabel: "Symbol",
      columnStyle: { width: "120px" },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <span className="text-body-secondary">{strategy.symbolName}</span>
      ),
    },
    {
      columnKey: "initialCapital",
      columnLabel: "Initial",
      columnStyle: { width: "100px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue value={strategy.initialCapital} type="currency" decimals={2} />
      ),
    },
    {
      columnKey: "currentCapital",
      columnLabel: "Current",
      columnStyle: { width: "100px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue value={strategy.currentCapital} type="currency" decimals={2} />
      ),
    },
    {
      columnKey: "pnl",
      columnLabel: "PnL",
      columnStyle: { width: "100px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue
          value={strategy.totalPnl}
          threshold={0}
          type="currency"
          decimals={2}
          showSign
        />
      ),
    },
    {
      columnKey: "pnlPct",
      columnLabel: "PnL %",
      columnStyle: { width: "80px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue
          value={strategy.totalPnlPct}
          threshold={0}
          type="percentage"
          decimals={2}
          showSign
        />
      ),
    },
    {
      columnKey: "winRate",
      columnLabel: "Win Rate",
      columnStyle: { width: "90px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue value={strategy.winRate * 100} type="percentage" decimals={1} />
      ),
    },
    {
      columnKey: "positions",
      columnLabel: "Trades",
      columnStyle: { width: "70px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue value={strategy.totalPositions} type="flat" />
      ),
    },
    {
      columnKey: "drawdown",
      columnLabel: "Max DD",
      columnStyle: { width: "80px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue
          value={-Math.abs(strategy.maxDrawdownPct)}
          type="percentage"
          decimals={2}
          forceColor="loss"
        />
      ),
    },
    {
      columnKey: "sharpe",
      columnLabel: "Sharpe",
      columnStyle: { width: "70px", textAlign: "right" as const },
      cellStyle: { textAlign: "right" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <ColoredValue value={strategy.sharpeRatio} type="number" decimals={2} />
      ),
    },
    {
      columnKey: "actions",
      columnLabel: "Actions",
      columnStyle: { width: "80px", textAlign: "center" as const },
      cellStyle: { textAlign: "center" as const },
      cellRender: (strategy: T.PortfolioTypes.StrategyPerformance) => (
        <CButton
          color="primary"
          size="sm"
          variant="outline"
          onClick={() => handleViewExecution(strategy.executionId)}
        >
          View
        </CButton>
      ),
    },
  ];

  return (
    <CDataTable
      className="StrategiesTable"
      columns={columns}
      items={strategies}
      striped
      small
      hover
      responsive
      getRowProps={(strategy) => ({ id: `strategy-${strategy.executionId}` })}
      getCellProps={(strategy, column) => ({
        id: `strategy-${strategy.executionId}-${column.columnKey}`,
      })}
    />
  );
};
